'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

const HOST_MOUNT = '#PW-StaffingHost';
const USER_PICKER = 'PS-UserPicker';
const ROLE_PICKER = 'PS-RolePicker';          // role for the "add staff" row
const MEMBER_ROLE_PICKER = 'PS-MemberRolePicker';   // role for an existing member's "add role"

/**
 * ProjectStaffing — the "Staffing" tab of the config-app Project workspace: manage which users are on a project and
 * their project role(s) (PE, Assistant PE, Contractor Approver, ADA, HQ AE, …). Mirrors the Home app's staffing page
 * and the member-sync model EXACTLY:
 *   - a member = one ProjectUserJoin {IDProject, IDUser, IDRole} (server computes the Synthesized* fields);
 *   - add a member: POST ProjectUserJoin { IDUser, IDProject, IDRole? };
 *   - a member's role(s) are UserContextRoles: add via PUT Permissions/Role/For/User {IDUser,Context:'Project',IDContext,IDRole},
 *     remove via DELETE Permissions/Role/:IDRole/For/User/:IDUser/In/Project/:IDContext;
 *   - remove a member: DELETE Permissions/Memberships/For/User/:IDUser/In/Project/:IDContext.
 * Project-assignable roles are Role records with Type IN ('','Role'). Gated by the User-Project / User-Role
 * association capabilities. Embedded via openInto(mount) — same host idiom as RecordDates.
 */
class ProjectStaffingProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._loadToken = 0;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID || tmpUser.IDCustomer) : 0; }

	_can(pEntity, pAction)
	{
		const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function')) { return tmpApp.EffectivePermissionsService.can(pEntity, pAction); }
		return true;
	}

	get state()
	{
		if (!this.pict.AppData.ProjectStaffing)
		{
			this.pict.AppData.ProjectStaffing =
			{
				IDProject: 0, IDCustomer: 0, MountSelector: HOST_MOUNT, LoadedKey: '',
				Loading: false, Loaded: false, Error: '',
				Members: [], AddUserID: '', AddRoleID: '', Adding: false, AddError: '',
				AddRoleForUser: 0, MemberRoleID: '', MemberBusy: 0, Notice: '',
				Can: { Associate: true, Disassociate: true, AssignRole: true, UnassignRole: true, ReadRoles: true },
			};
		}
		return this.pict.AppData.ProjectStaffing;
	}

	// ── REST helpers ─────────────────────────────────────────────────────────────
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteJSON(pURL) { const tmpRC = this.restClient; return (tmpRC && (typeof tmpRC.deleteJSON === 'function')) ? new Promise((resolve) => tmpRC.deleteJSON(pURL, (pErr) => resolve(!pErr))) : Promise.resolve(false); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	// Richer label for the user-SELECTION dropdown (and the resolved chip): name plus LoginID and Email so an
	// admin can pick the right person among similarly-named users. The members list uses the plain userLabel
	// (name) alongside its own Email column, so it is not cluttered.
	_userOptionLabel(pUser)
	{
		const tmpBase = libUserSearch.userLabel(pUser);
		const tmpBits = [];
		if (pUser.LoginID && (pUser.LoginID !== tmpBase))
		{
			tmpBits.push(pUser.LoginID);
		}
		if (pUser.Email && (pUser.Email !== tmpBase))
		{
			tmpBits.push(pUser.Email);
		}
		return tmpBits.length ? `${ tmpBase } — ${ tmpBits.join(' · ') }` : tmpBase;
	}

	// ── Open (workspace embed) ───────────────────────────────────────────────────
	openInto(pMode, pID, pMountSelector)
	{
		const tmpState = this.state;
		const tmpID = Number(pID) || 0;
		const tmpKey = `Project:${tmpID}`;
		const tmpMount = pMountSelector || HOST_MOUNT;
		// Same project already loaded (workspace repainted the host on a background render): re-attach, don't reload.
		if (tmpState.LoadedKey === tmpKey && tmpState.Loaded && !tmpState.Loading)
		{
			tmpState.MountSelector = tmpMount;
			this.render();
			return;
		}
		tmpState.MountSelector = tmpMount;
		tmpState.IDProject = tmpID;
		tmpState.LoadedKey = '';
		tmpState.Loading = true; tmpState.Loaded = false; tmpState.Error = '';
		tmpState.Members = []; tmpState.AddUserID = ''; tmpState.AddRoleID = ''; tmpState.AddError = '';
		tmpState.AddRoleForUser = 0; tmpState.MemberRoleID = ''; tmpState.Notice = '';
		tmpState.Can = {
			Associate: this._can('User-Project', 'Associate'), Disassociate: this._can('User-Project', 'Disassociate'),
			AssignRole: this._can('User-Role', 'Associate'), UnassignRole: this._can('User-Role', 'Disassociate'),
			ReadRoles: this._can('Role', 'Read'),
		};
		const tmpToken = ++this._loadToken;
		this.render();
		this._loadAsync(tmpToken);
	}

	async _loadAsync(pToken)
	{
		const tmpState = this.state;
		const tmpID = tmpState.IDProject;
		const fStale = () => (pToken !== undefined) && (pToken !== this._loadToken);
		if (!tmpID) { tmpState.Loading = false; tmpState.Error = 'No project specified.'; this.render(); return; }
		const tmpProject = await this._getJSON(`Project/${tmpID}`);
		if (fStale()) { return; }
		tmpState.IDCustomer = (tmpProject && Number(tmpProject.IDCustomer)) || this.callerCustomer();
		const tmpJoins = await this._getJSON(`ProjectUserJoins/FilteredTo/FBV~IDProject~EQ~${tmpID}/0/2000`);
		if (fStale()) { return; }
		const tmpJoinRows = Array.isArray(tmpJoins) ? tmpJoins : [];
		const tmpUserIDs = Array.from(new Set(tmpJoinRows.map((pJoin) => Number(pJoin.IDUser)).filter((pV) => pV > 0)));
		if (!tmpUserIDs.length) { tmpState.Members = []; tmpState.Loading = false; tmpState.Loaded = true; tmpState.LoadedKey = `Project:${tmpID}`; this.render(); return; }
		const tmpUsers = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpUserIDs.join(',')}/0/${tmpUserIDs.length + 10}`);
		if (fStale()) { return; }
		const tmpUserByID = {};
		(Array.isArray(tmpUsers) ? tmpUsers : []).forEach((pUser) => { if (pUser && pUser.IDUser != null) { tmpUserByID[Number(pUser.IDUser)] = pUser; } });
		// Resolve each member's project role(s) via the Permissions endpoint (mirrors the Home staffing cell).
		const tmpMembers = await Promise.all(tmpUserIDs.map(async (pIDUser) =>
		{
			const tmpRoles = await this._getJSON(`Permissions/Roles/For/User/${pIDUser}/In/Project/${tmpID}`);
			const tmpUser = tmpUserByID[pIDUser];
			return {
				IDUser: pIDUser,
				Name: tmpUser ? libUserSearch.userLabel(tmpUser) : `User #${pIDUser}`,
				Email: (tmpUser && tmpUser.Email) || '',
				Roles: (Array.isArray(tmpRoles) ? tmpRoles : [])
					.filter((pRole) => pRole && !this._isTruthy(pRole.Deleted) && pRole.Type !== 'UserGroup' && pRole.Type !== 'PermissionGroup')
					.map((pRole) => ({ IDRole: Number(pRole.IDRole), Name: pRole.Name || `Role #${pRole.IDRole}` })),
			};
		}));
		if (fStale()) { return; }
		tmpMembers.sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
		tmpState.Members = tmpMembers;
		tmpState.Loading = false; tmpState.Loaded = true; tmpState.LoadedKey = `Project:${tmpID}`;
		this.render();
	}

	_isTruthy(pVal) { return (pVal === true) || (pVal === 1) || (pVal === '1'); }
	_reload() { const tmpToken = ++this._loadToken; this._loadAsync(tmpToken); }

	// ── Add a member (user + optional role) ──────────────────────────────────────
	async addMemberAsync()
	{
		const tmpState = this.state;
		if (!tmpState.Can.Associate || tmpState.Adding) { return; }
		const tmpIDUser = Number(tmpState.AddUserID) || 0;
		if (!tmpIDUser) { tmpState.AddError = 'Pick a user to add.'; this.render(); return; }
		if ((tmpState.Members || []).some((pMember) => pMember.IDUser === tmpIDUser)) { tmpState.AddError = 'That user is already on this project.'; this.render(); return; }
		tmpState.Adding = true; tmpState.AddError = ''; tmpState.Notice = '';
		this.render();
		const tmpBody = { IDProjectUserJoin: 0, IDUser: tmpIDUser, IDProject: tmpState.IDProject };
		const tmpIDRole = Number(tmpState.AddRoleID) || 0;
		if (tmpIDRole > 0) { tmpBody.IDRole = tmpIDRole; }
		const tmpRes = await this._postJSON('ProjectUserJoin', tmpBody);
		tmpState.Adding = false;
		if (tmpRes.error) { tmpState.AddError = 'The server rejected the assignment (you may lack permission).'; this.render(); return; }
		tmpState.AddUserID = ''; tmpState.AddRoleID = ''; tmpState.Notice = 'Added.';
		this._reload();
	}

	// ── Per-member role management ───────────────────────────────────────────────
	openAddRole(pIDUser) { this.state.AddRoleForUser = Number(pIDUser) || 0; this.state.MemberRoleID = ''; this.render(); }
	cancelAddRole() { this.state.AddRoleForUser = 0; this.state.MemberRoleID = ''; this.render(); }

	async assignRoleAsync(pIDUser)
	{
		const tmpState = this.state;
		if (!tmpState.Can.AssignRole) { return; }
		const tmpIDRole = Number(tmpState.MemberRoleID) || 0;
		if (!tmpIDRole) { return; }
		tmpState.MemberBusy = Number(pIDUser);
		this.render();
		const tmpRes = await this._putJSON('Permissions/Role/For/User', { IDUser: Number(pIDUser), Context: 'Project', IDContext: tmpState.IDProject, IDRole: tmpIDRole });
		tmpState.MemberBusy = 0; tmpState.AddRoleForUser = 0; tmpState.MemberRoleID = '';
		if (tmpRes.error) { tmpState.Error = 'Could not assign the role.'; this.render(); return; }
		this._reload();
	}

	async removeRoleAsync(pIDUser, pIDRole)
	{
		const tmpState = this.state;
		if (!tmpState.Can.UnassignRole) { return; }
		tmpState.MemberBusy = Number(pIDUser);
		this.render();
		const tmpOK = await this._deleteJSON(`Permissions/Role/${Number(pIDRole)}/For/User/${Number(pIDUser)}/In/Project/${tmpState.IDProject}`);
		tmpState.MemberBusy = 0;
		if (!tmpOK) { tmpState.Error = 'Could not remove the role.'; this.render(); return; }
		this._reload();
	}

	async removeMemberAsync(pIDUser)
	{
		const tmpState = this.state;
		if (!tmpState.Can.Disassociate) { return; }
		tmpState.MemberBusy = Number(pIDUser);
		this.render();
		const tmpOK = await this._deleteJSON(`Permissions/Memberships/For/User/${Number(pIDUser)}/In/Project/${tmpState.IDProject}`);
		tmpState.MemberBusy = 0;
		if (!tmpOK) { tmpState.Error = 'Could not remove the member.'; this.render(); return; }
		this._reload();
	}

	// ── Pickers ──────────────────────────────────────────────────────────────────
	_pickerProvider() { return this.pict.providers['Pict-Section-Picker']; }

	/** Searchable Users picker over the shared user search (name, login ID or email; every word must match), scoped
	 *  to the caller's customer. */
	_userDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Scope: () => `FBV~IDCustomer~EQ~${this.callerCustomer()}~FBV~Deleted~EQ~0`,
				PageSize: 50,
				ToOption: (pUser) => ({ Value: Number(pUser.IDUser), Text: this._userOptionLabel(pUser), Record: pUser }),
			});
	}
	_userResolveValue() { return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`User/${tmpID}`).then((pUser) => ((pUser && pUser.IDUser) ? { Value: tmpID, Text: this._userOptionLabel(pUser) } : { Value: tmpID, Text: `#${tmpID}` })); }; }

	/** Project-assignable Role picker — Type IN ('','Role') (excludes UserGroup / PermissionGroup), customer-scoped. */
	_roleDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: 'Roles', IDField: 'IDRole', PageSize: 50, Scope: () => `FBV~IDCustomer~EQ~${this.callerCustomer()}~FBL~Type~INN~,Role`,
				ToOption: (pRole) => ({ Value: Number(pRole.IDRole), Text: pRole.Name || `Role #${pRole.IDRole}`, Record: pRole }),
			});
	}
	_roleResolveValue() { return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`Role/${tmpID}`).then((pRole) => ((pRole && pRole.IDRole) ? { Value: tmpID, Text: pRole.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })); }; }

	mountPickers()
	{
		const tmpState = this.state;
		const tmpPicker = this._pickerProvider();
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		const tmpCA = this.pict.ContentAssignment;
		if (tmpState.Can.Associate && (tmpCA.getElement(`#${USER_PICKER}`).length > 0))
		{
			const tmpUP = tmpPicker.createPicker(USER_PICKER, { DestinationAddress: `#${USER_PICKER}`, ValueAddress: 'AppData.ProjectStaffing.AddUserID', Searchable: true, AllowClear: true, ClearLabel: '— pick a user —', Placeholder: 'Search users by name, login ID, or email…', DataProvider: this._userDataProvider(), ResolveValue: this._userResolveValue() });
			if (tmpUP && (typeof tmpUP.render === 'function')) { tmpUP.render(); }
			if (tmpUP && (typeof tmpUP.setValue === 'function')) { tmpUP.setValue(tmpState.AddUserID || ''); }
		}
		if (tmpState.Can.Associate && tmpState.Can.ReadRoles && (tmpCA.getElement(`#${ROLE_PICKER}`).length > 0))
		{
			const tmpRP = tmpPicker.createPicker(ROLE_PICKER, { DestinationAddress: `#${ROLE_PICKER}`, ValueAddress: 'AppData.ProjectStaffing.AddRoleID', Searchable: true, AllowClear: true, ClearLabel: '— role (optional) —', Placeholder: 'Search roles…', DataProvider: this._roleDataProvider(), ResolveValue: this._roleResolveValue() });
			if (tmpRP && (typeof tmpRP.render === 'function')) { tmpRP.render(); }
			if (tmpRP && (typeof tmpRP.setValue === 'function')) { tmpRP.setValue(tmpState.AddRoleID || ''); }
		}
		if (tmpState.AddRoleForUser && (tmpCA.getElement(`#${MEMBER_ROLE_PICKER}`).length > 0))
		{
			const tmpMP = tmpPicker.createPicker(MEMBER_ROLE_PICKER, { DestinationAddress: `#${MEMBER_ROLE_PICKER}`, ValueAddress: 'AppData.ProjectStaffing.MemberRoleID', Searchable: true, AllowClear: true, ClearLabel: '— pick a role —', Placeholder: 'Search roles…', DataProvider: this._roleDataProvider(), ResolveValue: this._roleResolveValue() });
			if (tmpMP && (typeof tmpMP.render === 'function')) { tmpMP.render(); }
			if (tmpMP && (typeof tmpMP.setValue === 'function')) { tmpMP.setValue(tmpState.MemberRoleID || ''); }
		}
	}

	// ── Render ───────────────────────────────────────────────────────────────────
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpSel = this.state.MountSelector || HOST_MOUNT;
		if (tmpCA.getElement(tmpSel).length === 0) { return; }
		tmpCA.assignContent(tmpSel, this._html());
		this.mountPickers();
	}

	_html()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return '<div class="ps-wrap"><p class="ps-note">Loading…</p></div>'; }
		if (tmpState.Error) { return `<div class="ps-wrap"><p class="ps-error">${this._esc(tmpState.Error)}</p></div>`; }
		return `<div class="ps-wrap">${this._membersHTML()}${this._addHTML()}${tmpState.Notice ? `<div class="ps-notice">${this._esc(tmpState.Notice)}</div>` : ''}</div>`;
	}

	_membersHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Members.length) { return '<div class="ps-section"><div class="ps-section-head">Project staff</div><p class="ps-note">No one is assigned to this project yet.</p></div>'; }
		const tmpRows = tmpState.Members.map((pMember) =>
		{
			const tmpBusy = (tmpState.MemberBusy === pMember.IDUser);
			const tmpChips = pMember.Roles.length
				? pMember.Roles.map((pRole) => `<span class="ps-role">${this._esc(pRole.Name)}${tmpState.Can.UnassignRole ? `<button class="ps-x" title="Remove role" onclick="_Pict.providers.ProjectStaffing.removeRoleAsync(${pMember.IDUser}, ${pRole.IDRole})">×</button>` : ''}</span>`).join('')
				: '<span class="ps-norole">no role</span>';
			const tmpAddRole = (tmpState.Can.AssignRole && tmpState.Can.ReadRoles)
				? (tmpState.AddRoleForUser === pMember.IDUser
					? `<span class="ps-addrole"><span id="${MEMBER_ROLE_PICKER}" class="ps-rolepick"></span><button class="ps-btn ps-btn-sm ps-btn-primary" onclick="_Pict.providers.ProjectStaffing.assignRoleAsync(${pMember.IDUser})">Add</button><button class="ps-btn ps-btn-sm" onclick="_Pict.providers.ProjectStaffing.cancelAddRole()">Cancel</button></span>`
					: `<button class="ps-btn ps-btn-sm" onclick="_Pict.providers.ProjectStaffing.openAddRole(${pMember.IDUser})">+ role</button>`)
				: '';
			const tmpRemove = tmpState.Can.Disassociate ? `<button class="ps-icon ps-icon-x" title="Remove from project" onclick="_Pict.providers.ProjectStaffing.removeMemberAsync(${pMember.IDUser})">✕</button>` : '';
			return `<div class="ps-member${tmpBusy ? ' is-busy' : ''}"><div class="ps-member-main"><div class="ps-member-name">${this._esc(pMember.Name)}</div>${pMember.Email ? `<div class="ps-member-email">${this._esc(pMember.Email)}</div>` : ''}</div>`
				+ `<div class="ps-roles">${tmpChips} ${tmpAddRole}</div>${tmpRemove}</div>`;
		}).join('');
		return `<div class="ps-section"><div class="ps-section-head">Project staff <span class="ps-count">${tmpState.Members.length}</span></div>${tmpRows}</div>`;
	}

	_addHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Can.Associate) { return '<p class="ps-note">You can view staffing here, but adding members needs the manage-project-membership permission.</p>'; }
		const tmpBusy = tmpState.Adding ? '<span class="ps-busy">adding…</span>' : '';
		const tmpErr = tmpState.AddError ? `<div class="ps-error">${this._esc(tmpState.AddError)}</div>` : '';
		const tmpRolePick = tmpState.Can.ReadRoles ? `<span id="${ROLE_PICKER}" class="ps-rolepick"></span>` : '';
		return `<div class="ps-section ps-add"><div class="ps-section-head">Add staff</div>`
			+ `<div class="ps-add-row"><span id="${USER_PICKER}" class="ps-userpick"></span>${tmpRolePick}<button class="ps-btn ps-btn-primary" onclick="_Pict.providers.ProjectStaffing.addMemberAsync()">Add to project</button>${tmpBusy}</div>${tmpErr}</div>`;
	}
}

module.exports = ProjectStaffingProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectStaffing',
	AutoInitialize: true,
	AutoInitializeOrdinal: 45,
};
